import { faCalculator, faRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Checkbox, Empty, Flex, InputNumber, Modal, Skeleton, Space, Steps, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import {
  DistinctRateUnit,
  EnteredRateQuantities,
  getApplyDisabledReason,
  getSelectedChanges,
  hasAnyEnteredQuantity,
  isChangeSelected,
  LabeledTemplateGroup,
  NO_TEMPLATE_RATES_MESSAGE,
  QuantityChange,
  toggleChangeSelection
} from '../../../domain/productionRate'
import VSpace from '../../common/VSpace'
import Units from '../Units'

const { Text } = Typography

export type Props = {
  open: boolean
  unitGroups: LabeledTemplateGroup<DistinctRateUnit>[]
  isLoadingTemplateNames?: boolean
  getChangeGroups: (entered: EnteredRateQuantities) => LabeledTemplateGroup<QuantityChange>[]
  onClose: () => void
  onApply: (changes: QuantityChange[]) => void
}

const TemplateProductionRatesModal = ({ open, unitGroups, isLoadingTemplateNames, getChangeGroups, onClose, onApply }: Props) => {
  const [entered, setEntered] = useState<EnteredRateQuantities>({})
  const [excludedGuids, setExcludedGuids] = useState<string[]>([])
  const [isReviewing, setIsReviewing] = useState(false)

  useEffect(() => {
    setEntered({})
    setExcludedGuids([])
    setIsReviewing(false)
  }, [open])

  const changeGroups = isReviewing ? getChangeGroups(entered) : []
  const selectedChanges = getSelectedChanges(changeGroups, excludedGuids)

  const handleChange = (key: string, value: number | null) => {
    setEntered(current => ({ ...current, [key]: value }))
  }

  const handleSelectionChange = (guid: string, isSelected: boolean) => {
    setExcludedGuids(current => toggleChangeSelection(current, guid, isSelected))
  }

  const handleApplyPressed = () => {
    onClose()
    onApply(selectedChanges)
  }

  const groupHeader = (group: LabeledTemplateGroup<unknown>) => <Text strong>{group.name}</Text>

  const amountsStep = () => <VSpace>
    <Text type="secondary">Enter an amount for each unit to set the quantity on every item using that production rate. Leave a unit blank to skip it.</Text>
    {unitGroups.map(group => <VSpace key={group.key}>
      {groupHeader(group)}
      {group.items.map(unit => <Flex key={unit.key} align="center" gap="middle">
        <InputNumber
          value={entered[unit.key] ?? null}
          onChange={value => handleChange(unit.key, value)}
          addonAfter={<Units width={40} fontSize={11} units={unit.label} />}
          min={0}
          style={{ width: 180 }}
          onFocus={e => e.target.select()}
        />
        <Text type="secondary">{unit.itemCount === 1 ? '1 item' : `${unit.itemCount} items`}</Text>
      </Flex>)}
    </VSpace>)}
  </VSpace>

  const reviewStep = () => {
    if (changeGroups.length < 1) {
      return <Empty
        description="No items would be affected. These amounts match the quantities already set."
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    }

    return <VSpace>
      <Text type="secondary">These quantities will be updated when you apply. Uncheck any item you want to leave as it is.</Text>
      {changeGroups.map(group => <VSpace key={group.key}>
        {groupHeader(group)}
        {group.items.map(change => <Flex key={change.guid} align="center" justify="space-between" gap="middle">
          <Checkbox
            checked={isChangeSelected(excludedGuids, change.guid)}
            onChange={e => handleSelectionChange(change.guid, e.target.checked)}
          >
            {change.name}
          </Checkbox>
          <Space>
            <Text type="secondary">{change.currentQuantity}</Text>
            <FontAwesomeIcon icon={faRight} />
            <Text strong>{change.newQuantity}</Text>
            <Units width={40} fontSize={11} units={change.units} />
          </Space>
        </Flex>)}
      </VSpace>)}
    </VSpace>
  }

  const content = () => {
    if (unitGroups.length < 1) {
      return <Empty description={NO_TEMPLATE_RATES_MESSAGE} image={Empty.PRESENTED_IMAGE_SIMPLE} />
    }

    if (isLoadingTemplateNames) {
      return <Skeleton active paragraph={{ rows: 4 }} />
    }

    return <VSpace>
      <Steps
        size="small"
        current={isReviewing ? 1 : 0}
        items={[{ title: 'Amounts' }, { title: 'Review' }]}
        style={{ margin: '8px 0 16px 0' }}
      />
      {isReviewing ? reviewStep() : amountsStep()}
    </VSpace>
  }

  const footer = () => {
    if (unitGroups.length < 1) {
      return [<Button key="close" onClick={onClose}>Close</Button>]
    }

    if (isReviewing) {
      const disabledReason = getApplyDisabledReason(changeGroups.flatMap(x => x.items).length, selectedChanges.length)
      return [
        <Button key="back" onClick={() => setIsReviewing(false)}>Back</Button>,
        <Tooltip key="apply" title={disabledReason}>
          <Button type="primary" disabled={disabledReason !== ''} onClick={handleApplyPressed}>Apply</Button>
        </Tooltip>,
      ]
    }

    return [
      <Button key="cancel" onClick={onClose}>Cancel</Button>,
      <Button key="next" type="primary" disabled={!hasAnyEnteredQuantity(entered)} onClick={() => setIsReviewing(true)}>Next</Button>,
    ]
  }

  return <Modal
    open={open}
    onCancel={onClose}
    title={<Space style={{ fontSize: '1.2em' }}><FontAwesomeIcon icon={faCalculator} /> Template Production Rates</Space>}
    footer={footer()}
  >
    {content()}
  </Modal>
}

export default TemplateProductionRatesModal
